import './LobbyTable.styles.scss';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Knob } from 'primereact/knob';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, SyntheticEvent, useMemo, useRef, useState } from 'react';

import { GameType } from '../../../model/enumeration/GameType.enum';
import commonStore from '../../../store/CommonStore';
import { getTableColor } from '../../../utils/game-types.utils';
import AvailableBotsForSeat from '../../AvailableBotsForSit/AvailableBotsForSeat';
import { formatNumber } from '../../Game/table.render.utils';
import LongText from '../../LongText/LongText';
import WaitListButton from '../../shared/Button/WaitListButton/WaitListButton';
import { LobbyTableProps } from './LobbyTable.types';

const LobbyTable: FC<LobbyTableProps> = ({
  lobbyTable,
  onWaitListPlayersUpdate,
  width,
  height,
}) => {
  const [isAvailableBotsDialogVisible, setIsAvailableBotsDialogVisible] =
    useState(false);
  const unionOverlay = useRef(null);

  const isMtt = useMemo(
    () => lobbyTable.gameType.includes(GameType.MTT),
    [lobbyTable.gameType],
  );

  const onLobbyTableInfoClick = (e: SyntheticEvent): void => {
    e.stopPropagation();
    unionOverlay.current.toggle(e);
  };

  return (
    <div
      id={`lobby-table-${lobbyTable.tableId}`}
      className="LobbyTable-Component d-flex align-items-center cursor-pointer"
      style={{ width, height }}
      onClick={() => setIsAvailableBotsDialogVisible(true)}
    >
      <div
        className="game-type d-inline-flex justify-content-center align-items-center"
        style={{ background: getTableColor(lobbyTable.gameType) }}
      >
        {lobbyTable.gameType}
      </div>
      <Knob
        className={`max-player d-inline-flex me-3 ms-3 mt-2${
          isMtt ? ' mtt-font-size' : ''
        }`}
        value={lobbyTable.playerCount}
        strokeWidth={isMtt ? 0 : 10}
        valueColor={getTableColor(lobbyTable.gameType)}
        textColor="#ebecee"
        rangeColor="#4a4b4f"
        max={lobbyTable.maxPlayer}
        valueTemplate={`${lobbyTable.playerCount}/${lobbyTable.maxPlayer}`}
      />
      <div className="table-info-section d-inline-flex flex-column justify-content-center">
        <div className="table-name">
          <LongText
            text={lobbyTable.tableName}
            onCopyText="Table name copied to clipboard"
          />
        </div>
        <p className="sb-bb">
          {commonStore.convertLimit(lobbyTable.bb / 2)}/
          {commonStore.convertLimit(lobbyTable.bb)}
        </p>
        <p className="ante">ante: {formatNumber(lobbyTable.ante)}</p>
      </div>
      <div className="players-count-box">
        <div className="bots-amount">
          <img
            className="stat-icon"
            src="/assets/bot-stats.svg"
            alt="lp"
            width="24"
            height="24"
          />
          <span>{lobbyTable.playerCount - lobbyTable.livePlayersCount}</span>
        </div>
        <div className="players-amount">
          <img
            className="stat-icon"
            src="/assets/player-stats.svg"
            alt="lp"
            width="24"
            height="24"
          />
          <span>{lobbyTable.livePlayersCount}</span>
        </div>
      </div>
      <AvailableBotsForSeat
        fromLobbyTable={true}
        tableId={lobbyTable.tableId}
        room={lobbyTable.room}
        gameType={lobbyTable.gameType}
        limit={lobbyTable.bb}
        union={{
          club: lobbyTable.club,
          league: lobbyTable.league,
          superLeague: lobbyTable.superLeague,
        }}
        isTableFull={lobbyTable.playerCount === lobbyTable.maxPlayer}
        tablePlayerCount={lobbyTable.maxPlayer}
        isVisible={isAvailableBotsDialogVisible}
        onHide={() => setIsAvailableBotsDialogVisible(false)}
      />
      <Button
        className="lobby-table-info-btn"
        icon="pi pi-question-circle"
        rounded
        text
        onClick={onLobbyTableInfoClick}
      />
      <OverlayPanel
        className="union-overlay"
        ref={unionOverlay}
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-1">
          <span className="union-name">Table ID:</span>{' '}
          <span className="union-value">{lobbyTable.tableId}</span>
        </div>
        <div className="mb-1">
          <span className="union-name">Club:</span>{' '}
          <span className="union-value">{lobbyTable.club ?? '-'}</span>
        </div>
        <div className="mb-1">
          <span className="union-name">League:</span>{' '}
          <span className="union-value">{lobbyTable.league ?? '-'}</span>
        </div>
        <div className="mb-1">
          <span className="union-name">Super League:</span>{' '}
          <span className="union-value">{lobbyTable.superLeague ?? '-'}</span>
        </div>
      </OverlayPanel>
      <WaitListButton
        tableId={lobbyTable.tableId}
        isTableFull={lobbyTable.playerCount === lobbyTable.maxPlayer}
        room={lobbyTable.room}
        gameType={lobbyTable.gameType}
        limit={lobbyTable.bb}
        union={{
          club: lobbyTable.club,
          league: lobbyTable.league,
          superLeague: lobbyTable.superLeague,
        }}
        tablePlayerCount={lobbyTable.maxPlayer}
        fromLobbyTable={true}
        waitlistCount={lobbyTable.waitlistCount}
        onWaitListPlayersUpdate={onWaitListPlayersUpdate}
      />
    </div>
  );
};

export default observer(LobbyTable);
